import axios from "axios";
import {
  IGetWhatsappConversationsParams,
  ISendWhatsappMessageParams,
  ISendWhatsappFilesParams,
  IGetWhatsappStatsParams,
  IGetWhatsappMessageStatsParams,
} from "../models/whatsapp.model";

const inboxListParams = (params: IGetWhatsappConversationsParams) => ({
  page: params.page,
  limit: params.limit,
  keyword: params.keyword || "",
  status: params.status || "",
  ...(params.start_time ? { start_time: params.start_time } : {}),
  ...(params.campaign_id ? { campaign_id: params.campaign_id } : {}),
  ...(params.line_id ? { line_id: params.line_id } : {}),
  ...(params.agent_id ? { agent_id: params.agent_id } : {}),
  ...(params.assigned_to ? { assigned_to: params.assigned_to } : {}),
  ...(params.handled_by ? { handled_by: params.handled_by } : {}),
  ...(params.include_closed ? { include_closed: true } : {}),
});

export const getWhatsappConversations = async (
  params: IGetWhatsappConversationsParams
) => {
  const res = await axios.get("/ai-channel/messages", {
    params: {
      channel: "WHATSAPP_MESSAGE",
      ...inboxListParams(params),
    },
  });
  return res;
};

/**
 * `markRead` must only be set on the first load of a thread. This endpoint is
 * polled, and clearing the unread flag on every poll destroyed the unread state
 * of any reply that arrived between two polls.
 */
export const getWhatsappConversationById = async (
  id: string,
  markRead = false,
  extra?: { campaign_id?: string }
) => {
  const params: Record<string, string | boolean> = {};
  if (markRead) params.mark_read = true;
  if (extra?.campaign_id) params.campaign_id = extra.campaign_id;
  const res = await axios.get(`/ai-channel/${id}`, {
    params: Object.keys(params).length ? params : undefined,
  });
  return res;
};

export const sendWhatsappMessage = async (params: ISendWhatsappMessageParams) => {
  const res = await axios.post("/ai-channel/messages", params);
  return res;
};

export const sendWhatsappFiles = async (params: ISendWhatsappFilesParams) => {
  const formData = new FormData();
  
  // Append multiple files with field name 'files'
  params.files.forEach((file) => {
    formData.append("files", file);
  });
  
  formData.append("to", params.to);
  formData.append("from", params.from);
  formData.append("conversation_id", params.conversation_id);

  const res = await axios.post("/ai-channel/files", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return res;
};

export const updateWhatsappConversationStatus = async (
  id: string,
  status: string
) => {
  const res = await axios.patch(`/ai-channel/${id}`, { status });
  return res;
};

/** Claim a need-assign ticket for a team member (sets state open + assigned_to). */
export const assignWhatsappConversation = async (
  id: string,
  assignedTo: string
) => {
  const res = await axios.patch(`/ai-channel/${id}`, {
    assigned_to: assignedTo,
  });
  return res;
};

export const getWhatsappStats = async (params: IGetWhatsappStatsParams) => {
  const res = await axios.get("/ai-channel/stats_details", {
    params: {
      start_time: params.start_time,
      start_date: params.start_date || params.start_time,
      ...(params.channel ? { channel: params.channel } : {}),
    },
  });
  return res;
};

export const getInboxConversations = async (
  channel: string,
  params: IGetWhatsappConversationsParams
) => {
  const res = await axios.get("/ai-channel/messages", {
    params: {
      channel,
      ...inboxListParams(params),
    },
  });
  return res;
};

export const getWhatsappMessageStats = async (params: IGetWhatsappMessageStatsParams) => {
  const res = await axios.get("/message_stats", {
    params,
  });
  return res;
};